import { useEffect, useState } from 'react';
import { sessionsAPI, eventsAPI, speakersAPI, tracksAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Mic2 } from 'lucide-react';

export const SessionsAdminPage = () => {
  const [sessions, setSessions] = useState([]);
  const [events, setEvents] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [filterEventId, setFilterEventId] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    event_id: '',
    track_id: '',
    speaker_ids: [],
    description: '',
    start_time: '',
    end_time: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sessionsRes, eventsRes, speakersRes, tracksRes] = await Promise.all([
        sessionsAPI.getAll(),
        eventsAPI.getAll(),
        speakersAPI.getAll(),
        tracksAPI.getAll(),
      ]);
      setSessions(sessionsRes.data);
      setEvents(eventsRes.data);
      setSpeakers(speakersRes.data);
      setTracks(tracksRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // Filtered sessions based on selected event
  const filteredSessions = filterEventId === 'all' 
    ? sessions 
    : sessions.filter(s => s.event_id === filterEventId);

  // Get tracks available for the selected event in the form
  const getAvailableTracksForEvent = (eventId) => {
    if (!eventId) return tracks;
    const event = events.find(e => e.id === eventId);
    if (!event || !event.track_ids || event.track_ids.length === 0) {
      return tracks; // Return all tracks if no specific tracks configured
    }
    return tracks.filter(t => event.track_ids.includes(t.id));
  };

  const availableTracksForForm = getAvailableTracksForEvent(formData.event_id);

  const resetForm = () => {
    setFormData({
      title: '',
      event_id: '',
      track_id: '',
      speaker_ids: [],
      description: '',
      start_time: '',
      end_time: '',
    });
    setEditingSession(null);
  };

  const openEditDialog = (session) => {
    setEditingSession(session);
    setFormData({
      title: session.title,
      event_id: session.event_id,
      track_id: session.track_id,
      speaker_ids: session.speaker_ids || [],
      description: session.description || '',
      start_time: session.start_time,
      end_time: session.end_time,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSession) {
        await sessionsAPI.update(editingSession.id, formData);
        toast.success('Session aggiornata');
      } else {
        await sessionsAPI.create(formData);
        toast.success('Session creata');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Sei sicuro di voler eliminare questa session?')) return;
    try {
      await sessionsAPI.delete(id);
      toast.success('Session eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const toggleSpeaker = (speakerId) => {
    setFormData(prev => ({
      ...prev,
      speaker_ids: prev.speaker_ids.includes(speakerId)
        ? prev.speaker_ids.filter(id => id !== speakerId)
        : [...prev.speaker_ids, speakerId]
    }));
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.title || 'N/A';
  };

  const getTrackName = (trackId) => {
    const track = tracks.find(t => t.id === trackId);
    return track?.name || 'N/A';
  };

  return (
    <div className="space-y-6" data-testid="sessions-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Sessions</h1>
          <p className="text-muted-foreground">Gestisci le sessions SIOS</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-session-btn">
              <Plus size={18} className="mr-2" />
              Nuova Session
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingSession ? 'Modifica Session' : 'Nuova Session'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Evento *</Label>
                <Select
                  value={formData.event_id}
                  onValueChange={(value) => setFormData({ ...formData, event_id: value, track_id: '' })}
                >
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="select-event">
                    <SelectValue placeholder="Seleziona evento" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Track *</Label>
                <Select
                  value={formData.track_id}
                  onValueChange={(value) => setFormData({ ...formData, track_id: value })}
                  disabled={!formData.event_id}
                >
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="select-track">
                    <SelectValue placeholder={formData.event_id ? "Seleziona track" : "Seleziona prima un evento"} />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {availableTracksForForm.map((track) => (
                      <SelectItem key={track.id} value={track.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: track.color || '#7B2CBF' }}
                          />
                          {track.name}
                        </div>
                      </SelectItem>
                    ))}
                    {availableTracksForForm.length === 0 && (
                      <div className="p-2 text-sm text-muted-foreground">
                        Nessuna track configurata per questo evento
                      </div>
                    )}
                  </SelectContent>
                </Select>
                {formData.event_id && availableTracksForForm.length === 0 && (
                  <p className="text-xs text-yellow-500">
                    Questo evento non ha tracks configurate. Configurale nella pagina Eventi.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Titolo *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Titolo della session"
                  className="bg-white/5 border-white/10"
                  required
                  data-testid="input-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ora Inizio *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="bg-white/5 border-white/10"
                    required
                    data-testid="input-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ora Fine *</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="bg-white/5 border-white/10"
                    required
                    data-testid="input-end-time"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione della session..."
                  className="bg-white/5 border-white/10 min-h-24"
                  data-testid="input-description"
                />
              </div>

              <div className="space-y-2">
                <Label>Speakers</Label>
                <div className="max-h-48 overflow-y-auto space-y-2 p-2 border border-white/10 rounded-lg">
                  {speakers.map((speaker) => (
                    <div key={speaker.id} className="flex items-center gap-3">
                      <Checkbox
                        id={`speaker-${speaker.id}`}
                        checked={formData.speaker_ids.includes(speaker.id)}
                        onCheckedChange={() => toggleSpeaker(speaker.id)}
                        data-testid={`checkbox-speaker-${speaker.id}`}
                      />
                      <label 
                        htmlFor={`speaker-${speaker.id}`} 
                        className="text-sm cursor-pointer flex items-center gap-2"
                      >
                        <img 
                          src={speaker.image || `https://ui-avatars.com/api/?name=${speaker.first_name}+${speaker.last_name}&background=7B2CBF&color=fff&size=24`}
                          alt=""
                          className="w-6 h-6 rounded-full"
                        />
                        {speaker.first_name} {speaker.last_name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-session-btn">
                  {editingSession ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter by Event */}
      <div className="flex items-center gap-4">
        <Label className="text-sm font-medium">Filtra per evento:</Label>
        <Select
          value={filterEventId}
          onValueChange={setFilterEventId}
        >
          <SelectTrigger className="w-[300px] bg-white/5 border-white/10" data-testid="filter-event">
            <SelectValue placeholder="Tutti gli eventi" />
          </SelectTrigger>
          <SelectContent className="bg-card border-white/10">
            <SelectItem value="all">Tutti gli eventi</SelectItem>
            {events.map((event) => (
              <SelectItem key={event.id} value={event.id}>
                {event.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filterEventId !== 'all' && (
          <span className="text-sm text-muted-foreground">
            {filteredSessions.length} session{filteredSessions.length !== 1 ? 's' : ''} trovate
          </span>
        )}
      </div>

      {/* Table */}
      <div className="glass rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead>Titolo</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Track</TableHead>
              <TableHead>Orario</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredSessions.map((session) => (
              <TableRow key={session.id} className="border-white/5" data-testid={`session-row-${session.id}`}>
                <TableCell className="font-medium">{session.title}</TableCell>
                <TableCell>{getEventTitle(session.event_id)}</TableCell>
                <TableCell>{getTrackName(session.track_id)}</TableCell>
                <TableCell className="font-mono text-sm">
                  {session.start_time} - {session.end_time}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(session)}
                      data-testid={`edit-session-${session.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(session.id)}
                      data-testid={`delete-session-${session.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}

            {filteredSessions.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Mic2 className="mx-auto text-muted-foreground mb-4" size={32} />
                  <p className="text-muted-foreground">
                    {filterEventId !== 'all' ? 'Nessuna session per questo evento' : 'Nessuna session trovata'}
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
